import { PermissionEnum } from '@api/enum';
import { IProject, IUser } from '@api/models';
import { getExternalOnSearch, updateExternalProjects } from '@api/services/user.service';
import OverflowTooltip from '@components/OverflowTooltip/OverflowTooltip';
import useAuthPermission from '@hooks/use-auth-permission';
import { Button, CircularProgress, Input, List, ListItem } from '@hyperflake/react-ui-library';
import { CrossIcon } from '@icon/index';
import { Accordian, AccordianBody, AccordianCollapse, AccordianHeader, AccordianIcon } from '@library/accordian';
import { Switch } from '@library/forms';
import { useMutation } from '@tanstack/react-query';
import AutoComplete from 'modules/shared/components/AutoComplete/AutoComplete';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

interface ExternalProjectListProps {
    user?: IUser;
    onClose?: () => void;
    onUserUpdate?: (updatedUser: IUser) => void;
}

const ExternalProjectList = (props: ExternalProjectListProps) => {
    const { user } = props;

    const { clientId } = useParams();
    const { hasPermission } = useAuthPermission();

    const [fieldValue, setFieldValue] = useState('');
    const [externalProject, setExternalProject] = useState<IProject[]>([]);

    // Safe defaults
    const [selectedProjects, setSelectedProjects] = useState<IProject[]>([]);
    // projectId -> enabled assets
    const [projectAssetSelections, setProjectAssetSelections] = useState<Record<string, string[]>>({});

    // ---- Queries / mutations ----
    const externalProjectQuery = useMutation((searchParam: string) =>
        getExternalOnSearch(clientId, user?._id, searchParam)
    );

    // Allow payload to include projectAssetPermissions too
    const updateExternalProjectMutation = useMutation((payload: any) =>
        updateExternalProjects(clientId, user?._id, payload)
    );

    // ---- Helpers ----
    // Normalize assets from string[] | {name:string}[]
    const getAssetNames = (p?: IProject): string[] => {
        if (!p?.assets) return [];
        return (p.assets as any[]).map((a: any) => (typeof a === 'string' ? a : a?.name)).filter(Boolean);
    };

    // Prefer user's projectAssetPermissions; else fallback to ALL assets
    const getEnabledFromUser = (projId: string, _fallbackAssets: string[]) => {
        const fromUser = (user as any)?.projectAssetPermissions?.[projId];
        if (Array.isArray(fromUser) && fromUser.length > 0) return fromUser;
        return Array.isArray(_fallbackAssets) ? _fallbackAssets : [];
    };

    // ---- Effects ----
    // Seed selected projects from user.extProjects
    useEffect(() => {
        if (Array.isArray(user?.extProjects)) {
            setSelectedProjects(user!.extProjects as unknown as IProject[]);
        } else {
            setSelectedProjects([]);
        }
    }, [user?.extProjects]);

    // Seed/refresh enabled assets for selected projects from user.projectAssetPermissions
    // (only for those without a local selection already)
    useEffect(() => {
        setProjectAssetSelections((prev) => {
            const next = { ...prev };
            for (const p of selectedProjects ?? []) {
                if (!next[p._id]) {
                    const allAssets = getAssetNames(p);
                    next[p._id] = getEnabledFromUser(p._id, allAssets);
                }
            }
            return next;
        });
    }, [selectedProjects, user?.projectAssetPermissions]);

    // Debounced search
    useEffect(() => {
        const timeOutId = setTimeout(() => debouncedGetExternalProject(fieldValue), 500);
        return () => clearTimeout(timeOutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldValue]);

    // Exclude already selected projects from search results
    const filterProject = (newProjects: IProject[]) => {
        const filtered = newProjects.filter((project) => !selectedProjects.some((sel) => sel._id === project._id));
        setExternalProject(filtered);
    };

    const getExternalProject = async (q: string) => {
        try {
            const data = await externalProjectQuery.mutateAsync(q);
            filterProject(data);
        } catch (err: any) {
            setFieldValue('');
            setExternalProject([]);
            console.error(err);
        }
    };

    const debouncedGetExternalProject = (value: string) => {
        if (value.length > 1) getExternalProject(value);
    };

    // Toggle a single asset
    const onAssetToggle = (projectId: string, asset: string, checked: boolean) => {
        setProjectAssetSelections((prev) => {
            const current = new Set(prev[projectId] ?? []);
            if (checked) current.add(asset);
            else current.delete(asset);
            return { ...prev, [projectId]: Array.from(current) };
        });
    };

    // Remove a project
    const handleDelete = (projectId: string) => {
        setSelectedProjects((prev) => prev.filter((p) => p._id !== projectId));
        setProjectAssetSelections((prev) => {
            const { [projectId]: _, ...rest } = prev;
            return rest;
        });
    };

    // Persist
    const handleUpdate = async () => {
        const externalProjects = selectedProjects.map((p) => p._id);

        // Build explicit mapping of enabled assets per selected project
        const projectAssetPermissions: Record<string, string[]> = {};
        for (const p of selectedProjects) {
            const all = getAssetNames(p);
            const chosen = projectAssetSelections[p._id] ?? getEnabledFromUser(p._id, all);
            projectAssetPermissions[p._id] = chosen;
        }

        const payload: any = { externalProjects, projectAssetPermissions };

        try {
            await updateExternalProjectMutation.mutateAsync(payload);
            toast.success('External Project Updated successfully');
            // props.onUserUpdate?.(data);
            // props.onClose?.();
        } catch (err: any) {
            console.error(err?.response?.data?.message || err);
        }
    };

    return (
        <>
            <div>
                <div className="mt-2 text-base font-semibold">Manage {user?.name}'s External project.</div>
            </div>

            {/* Search / Add */}
            <div className="relative flex items-center w-full mt-2">
                <AutoComplete
                    renderInput={(inputProps) => <Input {...inputProps} placeholder="Search Projects" />}
                    value={fieldValue}
                    onChange={(value) => {
                        setFieldValue(value);

                        // debouncedGetExternalProject(value);
                    }}
                    onOptionSelect={(option) => {
                        setSelectedProjects((prevProjects: any) => [...prevProjects, option]);

                        // NEW: enable all assets for this project by default
                        setProjectAssetSelections((prev) => ({
                            ...prev,
                            [option._id]: getAssetNames(option), // all assets on
                        }));

                        setFieldValue('');
                        setExternalProject([]);
                    }}
                    options={externalProject}
                    renderOption={(option: any) => (
                        <div className="relative flex items-center w-full border-black max-h-10  bg-white ">
                            <List divider className="overflow-hidden ">
                                <ListItem className="w-full ">
                                    {option.name}-{option._id}
                                </ListItem>
                            </List>
                        </div>
                    )}
                    isLoading={externalProjectQuery.isLoading}
                    searchableFields={['name']}
                    disabled={!hasPermission(PermissionEnum.USER_UPDATE)}
                />
            </div>

            {/* Selected projects */}
            <div className="mt-4">
                <h5>External projects</h5>
                <List divider className="mt-2 h-72 overflow-y-auto">
                    {selectedProjects?.map((row: IProject) => {
                        const availableAssets = getAssetNames(row);
                        const enabledAssets =
                            projectAssetSelections[row._id] ?? getEnabledFromUser(row._id, availableAssets);

                        return (
                            <ListItem
                                key={row._id}
                                className="my-1 border border-primary/20 rounded-md"
                                style={{
                                    marginLeft: `${
                                        (row as any)?.hierarchyLevel ? (row as any).hierarchyLevel * 10 : 0
                                    }px`,
                                }}
                            >
                                <Accordian defaultExpanded className="mb-2 w-full">
                                    <AccordianHeader className="w-full" showIcon={false}>
                                        <div className="flex flex-col py-2">
                                            <div className="text-xs text-gray-500">{row._id}</div>
                                            <div className="text-base">{row.name}</div>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            <div className="flex items-center gap-2">
                                                <div className="text-gray-400 text-xs">Assets</div>
                                                <AccordianIcon />
                                            </div>

                                            <button
                                                className="p-1"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDelete(row._id);
                                                }}
                                            >
                                                <CrossIcon />
                                            </button>
                                        </div>
                                    </AccordianHeader>

                                    <AccordianCollapse>
                                        <AccordianBody className="p-4 bg-gray-100 rounded-lg">
                                            <div className="text-gray-400 text-xs font-semibold">Project Assets</div>

                                            {availableAssets.length === 0 ? (
                                                <div className="mt-2 text-xs text-gray-500">
                                                    No assets configured for this project.
                                                </div>
                                            ) : (
                                                <div className="flex flex-col gap-2 mt-2">
                                                    {availableAssets.map((asset) => {
                                                        const checked = enabledAssets.includes(asset);
                                                        return (
                                                            <div
                                                                key={`${row._id}-${asset}`}
                                                                className="text-sm font-semibold flex items-center justify-between"
                                                            >
                                                                <label
                                                                    htmlFor={`sel-asset-${row._id}-${asset}`}
                                                                    className="flex items-center gap-2 select-none overflow-hidden flex-1 cursor-pointer text-sm"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <OverflowTooltip className="font-semibold text-xs">
                                                                        {asset.replace(/_/g, ' ')}
                                                                    </OverflowTooltip>
                                                                </label>

                                                                <Switch
                                                                    id={`sel-asset-${row._id}-${asset}`}
                                                                    checked={checked}
                                                                    size="sm"
                                                                    onChange={(e) =>
                                                                        onAssetToggle(row._id, asset, e.target.checked)
                                                                    }
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    disabled={
                                                                        !hasPermission(PermissionEnum.USER_UPDATE)
                                                                    }
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </AccordianBody>
                                    </AccordianCollapse>
                                </Accordian>
                            </ListItem>
                        );
                    })}
                </List>
            </div>

            <Button
                type="submit"
                disabled={updateExternalProjectMutation.isLoading || !hasPermission(PermissionEnum.USER_UPDATE)}
                size="lg"
                className="mt-6 w-full sm:w-auto"
                onClick={handleUpdate}
            >
                {updateExternalProjectMutation.isLoading ? <CircularProgress size={20} color="inherit" /> : 'Save'}
            </Button>
        </>
    );
};

export default ExternalProjectList;
